import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { DeviceEntity, DeviceState } from './types';
import {
  LoadDataAction,
  EntityState,
  SortPayloadAction,
  DataPayload,
} from '../types';
import { genericFetch, structuredFetch } from '../../api/api';
import { get } from '../../hooks/useAxiosPrivate';
import { AxiosInstance } from 'axios';
import { Auth } from '../auth/types';
import { DeviceType } from './types';
import { isArtyc, isArtycAdmin } from '../auth';

const initialState: DeviceState = {
  data: [],
  currentPage: 0,
  resultsPerPage: 8,
  totalPages: 0,
  sortColumn: 'updatedAt',
  sortDirection: -1,
  selectedFilters: {},
  deviceTypeList: null,
  totalCompanySwitchPending: 0,
  totalCalibrationsNeedingReadings: 0,
  totalDevicesNeedingRecalibration: 0,
  totalDevicesPendingCertificate: 0,
};

const deviceSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    setDevices: (
      state,
      { payload }: PayloadAction<DataPayload<DeviceEntity>>
    ) => {
      state.data = payload.entities;
      state.totalPages = payload.totalPages;
    },
    setSearchFilter: (state, { payload }: PayloadAction<string>) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        search: payload === '' ? undefined : payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setDeviceTypeList: (state, { payload }: PayloadAction<DeviceType[]>) => {
      state.deviceTypeList = payload;
    },
    setDeviceTypesFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        deviceTypes: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setCompanyIdsFilter: (
      state,
      {
        payload,
      }: PayloadAction<{
        companyIds?: string[];
        excludeCompanyIds?: string[];
      }>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        companyIds: payload.companyIds,
        excludeCompanyIds: payload.excludeCompanyIds,
      });
      state.currentPage = initialState.currentPage;
    },
    setPendingCompanySwitchFilter: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        isCompanySwitchPendingFilter: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setNeedsCalibrationReadingsFilter: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        needsCalibrationReadingsFilter: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setNeedsRecalibrationFilter: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        needsRecalibrationFilter: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setNeedsCertificateFilter: (state, { payload }: PayloadAction<boolean>) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        needsCertificateFilter: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setTotalCompanySwitchPending: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.totalCompanySwitchPending = payload;
    },
    setTotalCalibrationsNeedingReadings: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.totalCalibrationsNeedingReadings = payload;
    },
    setTotalDevicesNeedingRecalibration: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.totalDevicesNeedingRecalibration = payload;
    },
    setTotalDevicesPendingCertificate: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.totalDevicesPendingCertificate = payload;
    },
    setProfileIdsFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        profileIds: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setModesFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        modes: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    sortDevices: (state, { payload }: SortPayloadAction<DeviceEntity>) => {
      state.sortColumn = payload.column;
      state.sortDirection = payload.direction;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    setDevicesPendingMode: (state, { payload }: PayloadAction<string[]>) => {
      const deviceIds = new Set(payload);
      state.data = state.data.map((device) =>
        deviceIds.has(device._id)
          ? { ...device, deviceMode: 'pending' as const }
          : device
      );
    },
    resetPage: (state) => {
      state.data = initialState.data;
      state.totalPages = initialState.totalPages;
      state.selectedFilters = initialState.selectedFilters;
      state.sortColumn = initialState.sortColumn;
      state.sortDirection = initialState.sortDirection;
      state.currentPage = initialState.currentPage;
    },
  },
});

export const createLoadDevices = (
  showEnvironments: boolean
): LoadDataAction<DeviceEntity> => {
  return async (
    axios: AxiosInstance,
    auth: Auth,
    state: EntityState<DeviceEntity>
  ) => {
    const {
      resultsPerPage,
      currentPage,
      sortColumn,
      sortDirection,
      selectedFilters,
    } = state;

    const useAllEnvs = isArtycAdmin(auth) && showEnvironments;

    const filters = {
      ...selectedFilters,
      ...(useAllEnvs ? { useAllEnvs } : {}),
    };

    try {
      const { entities, totalPages } = await structuredFetch<DeviceEntity>(
        axios,
        auth,
        'devices',
        resultsPerPage,
        currentPage,
        sortColumn,
        sortDirection,
        filters
      );

      return setDevices({ entities, totalPages });
    } catch (error) {
      console.error('Failed to load devices:', error);
      return setDevices({ entities: [], totalPages: 0 });
    }
  };
};

export const createLoadTotalCompanySwitchPending = () => {
  return async (axios: AxiosInstance, auth: Auth) => {
    // Artyc-only endpoint; always hit it via the artyc URL (genericFetch
    // would route an org-switched Artyc user to the empty company URL).
    if (!isArtyc(auth)) {
      return setTotalCompanySwitchPending(0);
    }
    try {
      const response = await get(
        axios,
        'artyc/devices/company-switch-pending',
        {}
      );
      return setTotalCompanySwitchPending(
        response.data.totalCompanySwitchPending
      );
    } catch (error) {
      console.error('Failed to load company switch pending count:', error);
      return setTotalCompanySwitchPending(0);
    }
  };
};

export const createLoadTotalCalibrationsNeedingReadings = () => {
  return async (axios: AxiosInstance, auth: Auth) => {
    try {
      const response = await genericFetch<{
        count: number;
      }>(
        axios,
        auth,
        '/api/v1/calibration/pending-readings/count',
        '/api/v1/calibration/pending-readings/count',
        {}
      );
      return setTotalCalibrationsNeedingReadings(response.count);
    } catch (error) {
      console.error(
        'Failed to load calibrations needing readings count:',
        error
      );
      return setTotalCalibrationsNeedingReadings(0);
    }
  };
};

export const createLoadTotalDevicesNeedingRecalibration = (
  showEnvironments = false
) => {
  return async (axios: AxiosInstance, auth: Auth) => {
    try {
      // Across-environments view (Artyc admin) → count production units.
      const useAllEnvs = isArtycAdmin(auth) && showEnvironments;
      const response = await genericFetch<{
        count: number;
      }>(
        axios,
        auth,
        '/api/v1/calibration/needs-recalibration/count',
        '/api/v1/calibration/needs-recalibration/count',
        useAllEnvs ? { useAllEnvs: 'true' } : {}
      );
      return setTotalDevicesNeedingRecalibration(response.count);
    } catch (error) {
      console.error(
        'Failed to load devices needing recalibration count:',
        error
      );
      return setTotalDevicesNeedingRecalibration(0);
    }
  };
};

export const createLoadTotalDevicesPendingCertificate = () => {
  return async (axios: AxiosInstance) => {
    try {
      // Same route for Artyc and company users (the server scopes by the
      // caller's company), so hit it directly instead of via genericFetch.
      const response = await get(
        axios,
        '/api/v1/calibration/pending-certificates/count',
        {}
      );
      return setTotalDevicesPendingCertificate(response.data.count);
    } catch (error) {
      console.error(
        'Failed to load calibrations pending certificate count:',
        error
      );
      return setTotalDevicesPendingCertificate(0);
    }
  };
};

export const {
  setDevices,
  setDeviceTypeList,
  setDeviceTypesFilter,
  setCompanyIdsFilter,
  setPendingCompanySwitchFilter,
  setNeedsCalibrationReadingsFilter,
  setNeedsRecalibrationFilter,
  setNeedsCertificateFilter,
  setTotalCompanySwitchPending,
  setTotalCalibrationsNeedingReadings,
  setTotalDevicesNeedingRecalibration,
  setTotalDevicesPendingCertificate,
  setProfileIdsFilter,
  setModesFilter,
  sortDevices,
  setSearchFilter,
  setCurrentPage,
  setDevicesPendingMode,
  resetPage,
} = deviceSlice.actions;

export default deviceSlice.reducer;
